import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { Button, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";

import { BsWallet2 } from "react-icons/bs";
import {
  FaExchangeAlt,
  FaMoneyBillWave
} from "react-icons/fa";
import { FaMoneyBillTransfer, FaRegMoneyBill1 } from "react-icons/fa6";
import { IoCashOutline, IoSettingsOutline } from "react-icons/io5";
import { CheckPermission } from "../../constants/const";
import { encryptStorage } from "../../constants/EncryptStorage";
import { GetLanguageString } from "../helper/Components";
import { isUsraRole, GetCustomStyles } from "../helper/Method";
import { ApiFinance } from "./ApiFinance";
import { ApiFund } from "./ApiFund";
import { CashAccountBalanceCard, AmanatAccountBalanceCard, MetricCard, parseBalanceNumber } from "./BalanceCards";

const QuickManageFund = ({ history }) => {
  const { t } = useTranslation();
  const unitTypeName = encryptStorage.getItem("unitTypeName");
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const [balances, setBalances] = useState({
    cashAccountBalance: 0,
    amanatAccountBalance: 0,
  });
  const [holding, setHolding] = useState(null);
  const [loadingBalances, setLoadingBalances] = useState(true);
  const usra = isUsraRole();
  const isMarkaz = unitTypeName === "Markaz" || loggedInUnitId === 0;
  const canManageFinanceConfig = isMarkaz && CheckPermission("CanManageFinanceConfig");

  const cashLimit = parseInt(balances?.cashLimit, 10) || 0;
  const cashEffectiveBalance = parseBalanceNumber(
    balances?.cash_effective_balance ??
    balances?.cashEffectiveBalance ??
    balances?.cashAccountBalance
  );
  const isCashLimitExceeded = !usra && cashLimit > 0 && cashEffectiveBalance > cashLimit;
  const excessCash = isCashLimitExceeded ? (cashEffectiveBalance - cashLimit) : 0;

  useEffect(() => {
    if (usra) {
      ApiFund()
        .GetUsraHolding(loggedInUnitId)
        .then((res) => setHolding(res.data))
        .catch(() => setHolding(null))
        .finally(() => setLoadingBalances(false));
      return;
    }
    ApiFinance()
      .GetCashToAmanatTransferData(loggedInUnitId)
      .then((res) => {
        if (res.data) {
          setBalances(res.data);
        }
      })
      .catch((err) => {
        console.error("Error fetching balances in QuickManageFund:", err);
      })
      .finally(() => {
        setLoadingBalances(false);
      });
  }, [loggedInUnitId, usra]);

  return (
    <>
      <div className="wrapper wrapper-content wrapper-custom">
        {/* Elegant side-by-side KPI Balance Cards */}
        <Row style={{ marginBottom: "15px" }}>
        {usra ? (
          <>
            <CashAccountBalanceCard
              balance={holding?.heldAmount || 0}
              loading={loadingBalances}
              labelKey="usra_cash_holding"
              badgeText="Held"
              colProps={{ md: "4", sm: "12", className: "mb-3" }}
            />
            <MetricCard
              labelKey="usra_receipt_count"
              badgeText="Receipts"
              value={holding?.heldCount || 0}
              format="integer"
              tone="slate"
              loading={loadingBalances}
              colProps={{ md: "4", sm: "12", className: "mb-3" }}
              icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#39576B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6M8 13h8M8 17h8" />
                </svg>
              }
            />
            <MetricCard
              labelKey="usra_oldest_age"
              badgeText="Days"
              value={holding?.oldestAgeDays || 0}
              format="days"
              tone="emerald"
              loading={loadingBalances}
              colProps={{ md: "4", sm: "12", className: "mb-3" }}
              icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#237A6E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              }
            />
            {holding?.isBlocked ? (
              <Col md="12" className="mb-2">
                <p className="text-warning mb-0">
                  <GetLanguageString props="usra_holding_limit_warning" />
                </p>
              </Col>
            ) : holding?.amountWarning || holding?.daysWarning ? (
              <Col md="12" className="mb-2">
                <p className="text-warning mb-0">
                  <GetLanguageString props="usra_holding_warning" />
                </p>
              </Col>
            ) : null}
          </>
        ) : (
          <>
          <CashAccountBalanceCard
            balance={balances.cashAccountBalance}
            effectiveBalance={balances.cash_effective_balance ?? balances.cashEffectiveBalance ?? balances.cashAccountBalance}
            ledgerBalance={balances.cash_ledger_balance ?? balances.cashLedgerBalance ?? balances.cashAccountBalance}
            pendingHolds={balances.cash_pending_holds ?? balances.cashPendingHolds ?? 0}
            loading={loadingBalances}
            colProps={{ md: "6", sm: "12", className: "mb-3" }}
          />
          <AmanatAccountBalanceCard
            balance={balances.amanatAccountBalance}
            effectiveBalance={balances.amanat_effective_balance ?? balances.amanatEffectiveBalance ?? balances.amanatAccountBalance}
            ledgerBalance={balances.amanat_ledger_balance ?? balances.amanatLedgerBalance ?? balances.amanatAccountBalance}
            pendingHolds={balances.amanat_pending_holds ?? balances.amanatPendingHolds ?? 0}
            loading={loadingBalances}
            colProps={{ md: "6", sm: "12", className: "mb-3" }}
          />
          </>
        )}
        </Row>

        {!usra && !loadingBalances && isCashLimitExceeded && (
          <Row style={{ marginBottom: "16px" }}>
            <Col md="12">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px",
                  padding: "10px 14px",
                  background: "#FFFDF5",
                  border: "1px solid #FDE68A",
                  borderLeft: "4px solid #F59E0B",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  fontSize: "13px",
                  lineHeight: "1.45",
                  color: "#78350F",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: "260px" }}>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      background: "#FEF3C7",
                      color: "#D97706",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                  <div>
                    <strong style={{ fontWeight: 600, color: "#92400E", marginRight: "4px" }}>
                      {t("cash_limit_exceeded_warning_title", "Warning: Cash in Hand Limit Exceeded.")}
                    </strong>
                    {t(
                      "cash_limit_exceeded_warning_body",
                      `Your current Cash in Hand balance (Rs ${cashEffectiveBalance.toLocaleString()}) exceeds your authorized holding limit (Rs ${cashLimit.toLocaleString()}) by Rs ${excessCash.toLocaleString()}. Per the finance module, please deposit the excess amount into your Amanat Account immediately.`,
                      {
                        cash: cashEffectiveBalance.toLocaleString(),
                        limit: cashLimit.toLocaleString(),
                        excess: excessCash.toLocaleString(),
                      }
                    )}
                  </div>
                </div>
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => history.push("/funds/transferAmanatToCashInHand")}
                  style={{
                    fontWeight: 600,
                    fontSize: "12px",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    whiteSpace: "nowrap",
                    backgroundColor: "#D97706",
                    borderColor: "#D97706",
                    color: "#FFFFFF",
                  }}
                >
                  {t("transfer_to_amanat_account_btn", "Transfer to Amanat Account →")}
                </Button>
              </div>
            </Col>
          </Row>
        )}

        {canManageFinanceConfig && (
          <Row style={{ marginBottom: "15px" }}>
            <Col md="12" className="d-flex justify-content-end">
              <Button
                color="white"
                size="sm"
                className={GetCustomStyles().btn_style}
                onClick={() =>
                  history.push({
                    pathname: `/funds/financeConfig`,
                  })
                }
              >
                <IoSettingsOutline
                  color="#18A689"
                  size={17}
                  style={{ marginRight: 5, marginBottom: 1 }}
                />
                <GetLanguageString props="finance_tab_finance_config" />
              </Button>
            </Col>
          </Row>
        )}

        <div className="ibox">
          <div className="ibox-content panel-body">
            <h3>{<GetLanguageString props="transactions_income" />}</h3>

            <Row>
              <Col xl="1" className="text-center">
                <Button
                  className="btn-danger dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/addInfaqCashInHand`,
                    })
                  }
                >
                  <FaRegMoneyBill1 size={50} />
                </Button>
                <h4>
                  {
                    <GetLanguageString props="transactions_infaq_to_cash_in_hand" />
                  }
                </h4>
              </Col>

              <Col xl="1" className="text-center">
                <Button
                  className="btn-success dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/addBulkInfaq`,
                    })
                  }
                >
                  <FaMoneyBillWave size={50} />
                </Button>
                <h4>{<GetLanguageString props="funds_add_bulk_fund" />}</h4>
              </Col>

              {!isUsraRole() && (
              <Col xl="1" className="text-center">
                <Button
                  className="btn-info dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/addInfaqAmanatAccount`,
                    })
                  }
                >
                  <FaRegMoneyBill1 size={50} />
                </Button>
                <h4>
                  {
                    <GetLanguageString props="transactions_infaq_to_amanat_account" />
                  }
                </h4>
              </Col>
              )}

              {isUsraRole() && (
              <Col xl="1" className="text-center">
                <Button
                  className="btn-warning dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/requestUsraRemittance`,
                    })
                  }
                >
                  <FaMoneyBillTransfer size={50} />
                </Button>
                <h4>
                  {<GetLanguageString props="usra_forward_cash" />}
                </h4>
              </Col>
              )}

              {unitTypeName === "Tanzeem" && !isUsraRole() && (
              <Col xl="1" className="text-center">
                <Button
                  className="btn-warning dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/incomingUsraRemittances`,
                    })
                  }
                >
                  <FaMoneyBillTransfer size={50} />
                </Button>
                <h4>
                  {<GetLanguageString props="usra_incoming_remittances" />}
                </h4>
              </Col>
              )}

              {isUsraRole() ? (
              <Col xl="1" className="text-center">
                <Button
                  className="btn-primary dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/manageUsraInfaq`,
                    })
                  }
                >
                  <BsWallet2 size={50} />
                </Button>
                <h4>{<GetLanguageString props="manage_infaq" />}</h4>
              </Col>
              ) : (
              <Col xl="1" className="text-center">
                <Button
                  className="btn-primary dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/manageIncome`,
                    })
                  }
                >
                  <BsWallet2 size={50} />
                </Button>
                <h4>{<GetLanguageString props="manage_income" />}</h4>
              </Col>
              )}
              {!isUsraRole() && (
                <>
                  <Col xl="1" className="text-center">
                    <Button
                      className="btn-success dim btn-large-dim"
                      onClick={() =>
                        history.push({
                          pathname: `/funds/otherIncome`,
                        })
                      }
                    >
                      <IoCashOutline size={50} />
                    </Button>
                    <h4>{<GetLanguageString props="funds_other_income" />}</h4>
                  </Col>
                  {unitTypeName === "Halqa" && (
                    <Col xl="1" className="text-center">
                      <Button
                        className="btn-warning dim btn-large-dim"
                        onClick={() =>
                          history.push({
                            pathname: `/funds/otherIncomeLoanFromMarkaz`,
                          })
                        }
                      >
                        <IoCashOutline size={50} />
                      </Button>
                      <h4>
                        {
                          <GetLanguageString props="funds_other_income_loan_from_markaz" />
                        }
                      </h4>
                    </Col>
                  )}
                  {unitTypeName === "Halqa" && (
                    <>
                      <Col xl="1" className="text-center">
                        <Button
                          className="btn-primary dim btn-large-dim"
                          onClick={() =>
                            history.push({
                              pathname: `/funds/OtherIncomeTarbiatiCourseReimbursment`,
                            })
                          }
                        >
                          <IoCashOutline size={50} />
                        </Button>
                        <h4>
                          {
                            <GetLanguageString props="funds_other_income_tarbiati_course_reimbursment" />
                          }
                        </h4>
                      </Col>
                      <Col xl="1" className="text-center">
                        <Button
                          className="btn-success dim btn-large-dim"
                          onClick={() =>
                            history.push({
                              pathname: `/funds/otherIncomeHelpFromMuqamiTanzeem`,
                            })
                          }
                        >
                          <IoCashOutline size={50} />
                        </Button>
                        <h4>
                          {
                            <GetLanguageString props="funds_other_income_assistance_from_muqami_tanzeem" />
                          }
                        </h4>
                      </Col>
                    </>
                  )}
                  <Col xl="1" className="text-center">
                    <Button
                      className="btn-warning dim btn-large-dim"
                      onClick={() =>
                        history.push({
                          pathname: `/funds/assetDonation`,
                        })
                      }
                    >
                      <IoCashOutline size={50} />
                    </Button>
                    <h4>
                      {<GetLanguageString props="funds_asset_donation" />}
                    </h4>
                  </Col>
                </>
              )}
            </Row>
          </div>
        </div>
      </div>

      {!isUsraRole() && (
        <div
          style={{ marginTop: "-50px", padding: "5px" }}
          className="wrapper wrapper-content wrapper-custom"
        >
          <div className="ibox">
            <div className="ibox-content panel-body">
              <h3>{<GetLanguageString props="sidebar_funds_expenses" />}</h3>

              <Row>
                <Col xl="1" className="text-center">
                  <Button
                    className="btn-success dim btn-large-dim"
                    onClick={() =>
                      history.push({
                        pathname: `/funds/expenseFromCashInHand`,
                      })
                    }
                  >
                    <IoCashOutline size={50} />
                  </Button>
                  <h4>
                    {
                      <GetLanguageString props="finance_expense_cash_in_hand" />
                    }
                  </h4>
                </Col>
                <Col xl="1" className="text-center">
                  <Button
                    className="btn-info dim btn-large-dim"
                    onClick={() =>
                      history.push({
                        pathname: `/funds/expenseFromAmanat`,
                      })
                    }
                  >
                    <IoCashOutline size={50} />
                  </Button>
                  <h4>
                    {
                      <GetLanguageString props="finance_expense_amanat_account" />
                    }
                  </h4>
                </Col>
                {unitTypeName === "Tanzeem" && (
                  <Col xl="1" className="text-center">
                    <Button
                      className="btn-warning dim btn-large-dim"
                      onClick={() =>
                        history.push({
                          pathname: `/funds/expenseHalqaShare`,
                        })
                      }
                    >
                      <IoCashOutline size={50} />
                    </Button>
                    <h4>
                      {
                        <GetLanguageString props="funds_halqa_share" />
                      }
                    </h4>
                  </Col>
                )}
                <Col xl="1" className="text-center">
                  <Button
                    className="btn-primary dim btn-large-dim"
                    onClick={() =>
                      history.push({
                        pathname: `/funds/manageExpense`,
                      })
                    }
                  >
                    <BsWallet2 size={50} />
                  </Button>
                  <h4>{<GetLanguageString props="manage_expense" />}</h4>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}

      {!isUsraRole() && (
      <div
        style={{ marginTop: "-10px", padding: "5px" }}
        className="wrapper wrapper-content wrapper-custom"
      >
        <div className="ibox">
          <div className="ibox-content panel-body">
            <h3>{<GetLanguageString props="transfer" />}</h3>

            <Row>
              <Col xl="1" className="text-center">
                <Button
                  className="btn-warning dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/transferCashInHandToAmanat`,
                    })
                  }
                >
                  <FaMoneyBillTransfer />
                </Button>
                <h4>
                  {
                    <GetLanguageString props="transactions_cashinhand_to_amanat_account" />
                  }
                </h4>
              </Col>
              <Col xl="1" className="text-center">
                <Button
                  className="btn-danger dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/transferAmanatToCashInHand`,
                    })
                  }
                >
                  <FaMoneyBillTransfer />
                </Button>
                <h4>
                  {
                    <GetLanguageString props="transactions_amanat_to_cash_in_hand_account" />
                  }
                </h4>
              </Col>
              {unitTypeName === "Halqa" && (
                <Col xl="1" className="text-center">
                  <Button
                    className="btn-success dim btn-large-dim"
                    onClick={() =>
                      history.push({
                        pathname: `/funds/transferMarkazShare`,
                      })
                    }
                  >
                    <FaMoneyBillTransfer />
                  </Button>
                  <h4>
                    {
                      <GetLanguageString props="funds_markaz_share" />
                    }
                  </h4>
                </Col>
              )}
              <Col xl="1" className="text-center">
                <Button
                  className="btn-info dim btn-large-dim"
                  onClick={() =>
                    history.push({
                      pathname: `/funds/manageTransfer`,
                    })
                  }
                >
                  <FaExchangeAlt />
                </Button>
                <h4>
                  {<GetLanguageString props="transactions_manage_transfer" />}
                </h4>
              </Col>
              {canManageFinanceConfig && (
                <Col xl="1" className="text-center">
                  <Button
                    className="btn-primary dim btn-large-dim"
                    onClick={() =>
                      history.push({
                        pathname: `/funds/financeConfig`,
                      })
                    }
                  >
                    <IoSettingsOutline size={50} />
                  </Button>
                  <h4>
                    {
                      <GetLanguageString props="finance_tab_finance_config" />
                    }
                  </h4>
                </Col>
              )}
            </Row>
          </div>
        </div>
      </div>
      )}
    </>
  );
};

export default withRouter(QuickManageFund);
